import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { fetchRatings } from "../slice/ratingSlice";
import { createBook } from "../slice/bookSlice";
import useLoginCheck from "../util/useLoginCheck";

const NewBook = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const googleBook = location.state?.googleBook;

  const [dateRead, setDateRead] = useState("");
  const [ratings, setRatings] = useState([]);
  const [rating, setRating] = useState({});
  const [ratingId, setRatingId] = useState(-1);
  const [review, setReview] = useState("");
  const [quotes, setQuotes] = useState([""]);
  const [savingBook, setSavingBook] = useState(false);

  const reduxRatings = useSelector((state) => state.ratings.ratings);

  useLoginCheck();

  useEffect(() => {
    if (reduxRatings?.length > 0) {
      setRatings(reduxRatings);
    }
  }, [reduxRatings]);

  useEffect(() => {
    dispatch(fetchRatings());
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSavingBook(true);
    const postData = {
      title: googleBook?.title,
      review,
      dateRead,
      rating,
      quotes: quotes.filter((q) => q.trim() !== ""),
      ...(googleBook && {
        googleVolumeId: googleBook.googleVolumeId,
        isbn13: googleBook.isbn13,
        thumbnailUrl: googleBook.thumbnailUrl,
        authors: googleBook.authors,
      }),
    };
    const resultAction = await dispatch(createBook(postData));
    if (createBook.fulfilled.match(resultAction)) {
      navigate(`/books/${resultAction.payload.sid}`);
    } else {
      setSavingBook(false);
    }
  };

  const handleAddQuote = () => setQuotes([...quotes, ""]);

  const handleQuoteChange = (index, value) => {
    const newQuotes = quotes.slice();
    newQuotes[index] = value;
    setQuotes(newQuotes);
  };

  const handleRemoveQuote = (index) => {
    setQuotes([...quotes.slice(0, index), ...quotes.slice(index + 1)]);
  };

  const handleRatingSelected = (event) => {
    const value = parseInt(event.target.value, 10);
    const selectedRating = ratings.find((r) => r.value === value);
    setRatingId(value);
    setRating(selectedRating);
  };

  const inputClasses =
    "w-full border border-stone-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600";

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-6">
      <div className="card p-4 md:p-6 space-y-4">
        {googleBook?.thumbnailUrl && (
          <img
            src={googleBook.thumbnailUrl}
            alt={googleBook.title}
            className="h-32 mb-2"
          />
        )}
        <div className="font-medium text-stone-800">{googleBook?.title}</div>
        {googleBook?.authors?.length > 0 && (
          <div className="text-sm text-stone-500 -mt-2">
            {googleBook.authors.join(", ")}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <select
            value={ratingId}
            onChange={handleRatingSelected}
            className={inputClasses}
          >
            <option value="-1" disabled>
              Rating
            </option>
            {reduxRatings.map((r) => (
              <option key={r.value} value={r.value}>
                {r.description}
              </option>
            ))}
          </select>
          <Flatpickr
            className={inputClasses}
            value={dateRead}
            onChange={(selectedDates) => setDateRead(selectedDates[0])}
            options={{ dateFormat: "Y-m-d" }}
          />
          <textarea
            placeholder="Review"
            value={review}
            onChange={(e) => setReview(e.target.value)}
            required
            className={`${inputClasses} min-h-[120px]`}
          />
          <div className="space-y-2">
            {quotes.map((quote, index) => (
              <div key={index} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  className={`${inputClasses} flex-1`}
                  placeholder="Quote"
                  value={quote}
                  onChange={(e) => handleQuoteChange(index, e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveQuote(index)}
                  className="btn-secondary shrink-0"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddQuote}
              className="btn-secondary"
            >
              Add Quote
            </button>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={savingBook}
              className="btn-primary"
            >
              {savingBook ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewBook;
